import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { clearCurrentUser } from "../hooks/useCurrentUser";
import { IconMail, IconLock, IconAlert, IconLoader, IconCheck } from "../components/Icons";
import LanguageFlags from "../components/LanguageFlags";
import logoUrl from "/luigipos-lockup-dark.svg";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState<{
    show: boolean;
    canResend: boolean;
    message: string;
  } | null>(null);
  const [resendSent, setResendSent] = useState(false);
  const nav = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setVerification(null);
    setResendSent(false);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_email_verification) {
        setVerification({
          show: true,
          canResend: data.can_resend_verification,
          message: data.message || t("auth.emailNotVerified"),
        });
        return;
      }
      localStorage.setItem("lk_access", data.access_token);
      localStorage.setItem("lk_refresh", data.refresh_token);
      clearCurrentUser();
      nav("/");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  }

  async function resendVerification() {
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setResendSent(true);
    } catch (e) {
      setErr(apiError(e, t("auth.resendError")));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", minHeight: "100vh", background: "var(--bg)" }}>
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 10 }}>
        <LanguageFlags />
      </div>
      <div style={{
        flex: "1 1 360px",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-elevated)",
        padding: 40,
      }}>
        <img
          src={logoUrl}
          alt="LuigiPOS"
          style={{ maxWidth: "80%", maxHeight: "45vh", width: "auto", height: "auto", objectFit: "contain" }}
        />
      </div>

      <div style={{
        flex: "1 1 360px",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <div className="card card-glow" style={{ padding: 32 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t("auth.loginTitle")}</h1>
            <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 24 }}>
              {t("auth.loginSubtitle")}
            </p>

            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {t("auth.email")} <IconMail size={16} className="muted" />
                </label>
                <input
                  type="email" placeholder="admin@firma.cz" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {t("auth.password")} <IconLock size={16} className="muted" />
                </label>
                <input
                  type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {err && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--red-dim)", color: "var(--red)", fontSize: 13,
                }}>
                  <IconAlert size={16} />
                  {err}
                </div>
              )}

              {verification && (
                <div style={{
                  padding: "14px", borderRadius: "var(--radius-sm)",
                  background: "var(--yellow-dim)", color: "var(--yellow)", fontSize: 13,
                }}>
                  <div style={{ marginBottom: 10 }}>{verification.message}</div>
                  {verification.canResend && !resendSent && (
                    <button type="button" className="primary" onClick={() => void resendVerification()} disabled={loading}>
                      {loading ? <IconLoader size={16} /> : t("auth.resendVerification")}
                    </button>
                  )}
                  {resendSent && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--green)" }}>
                      <IconCheck size={16} />
                      {t("auth.verificationSent")}
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="primary" disabled={loading} style={{ marginTop: 4 }}>
                {loading ? <IconLoader size={18} /> : t("auth.signIn")}
              </button>
            </form>

            <p style={{ marginTop: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
              {t("auth.noAccount")} <Link to="/register" style={{ color: "var(--accent)", fontWeight: 600 }}>{t("auth.register")}</Link>
            </p>
            <p style={{ marginTop: 12, textAlign: "center", fontSize: 14 }}>
              <Link to="/forgot-password" style={{ color: "var(--text-secondary)" }}>{t("auth.forgotLink")}</Link>
            </p>
          </div>

          <p style={{ textAlign: "center", marginTop: 24, color: "var(--muted)", fontSize: 12 }}>
            © 2026 LuigiPOS — {t("auth.footerTagline")}
          </p>
        </div>
      </div>
    </div>
  );
}
